/**
 * Palette variables — selectors for the rule-editor's variable chip palette.
 *
 * The Formula node's `inputsConfig.formula.options` exposes a list of
 * variables (one per chip). Each variable carries an `expose` block with:
 *   - kind  — display category ("Comisiones", "Monedas", "Lista de Precios",
 *             "Items en esta publicación", ...)
 *   - label — user-facing chip label
 *   - value — token inserted into the formula textbox when the chip is picked
 *
 * The runtime materialization (readmodel + transform pipeline) produces
 * `MaterializedVariable` rows from those declarations. Today the editor
 * renders all chips in a single flat list; this module groups them by
 * `expose.kind` so the palette can show a section header per kind.
 *
 * Grouping is data-driven: any new `kind` that appears in the materialized
 * variable list shows up automatically — no hardcoded list of kinds.
 */

/**
 * A single materialized variable chip.
 *
 * Mirrors the shape produced by the rules engine when expanding a
 * `RuleNodeDeclarationValuesType`'s `expose` template against a readmodel
 * row. `kind` is optional in practice — legacy declarations may omit it.
 */
export type MaterializedVariable = {
  kind?: string;
  label: string;
  value: string;
  description?: string;
};

/**
 * Section header + ordered chips for one `expose.kind` group.
 *
 * `kind` is the verbatim `expose.kind` value from the YAML (or
 * `GENERAL_KIND` for chips that omit it). The palette renders this string
 * directly as the section header — kind values in the YAML are already
 * user-facing copy ("Comisiones", "Items en esta publicación", ...).
 */
export type VariableGroup = {
  kind: string;
  variables: MaterializedVariable[];
};

/**
 * Comparator that decides chip order within a single `expose.kind` group.
 *
 * The comparator is a pluggable predicate — Ticket 4 (this ticket) ships
 * `defaultLabelComparator` (alphabetical by label) as the universal fallback.
 *
 * Ticket 2 will introduce `kind: 'Items en esta publicación'` chips that
 * must sort by kit position (variation order, then item order within
 * variation, with the main item — `variations[0].kitItems[0]` — first per
 * ADR-017). When that lands, callers can pass a kit-aware comparator
 * keyed on `kind` (e.g. via `kindAwareComparator` below) without
 * touching the grouping code in this file or the rendering code in
 * `VariablesPalette`.
 */
export type VariableComparator = ( a: MaterializedVariable, b: MaterializedVariable ) => number;

/**
 * Fallback "General" section label for chips that lack `expose.kind`.
 *
 * Acceptance criterion 2: chips without `expose.kind` fall into a
 * "General" section (never error). The palette renders this constant
 * verbatim — no translation in this ticket per the ticket spec.
 */
export const GENERAL_KIND = 'General';

/**
 * Default sort comparator: alphabetical by `label` (locale-aware).
 *
 * Used when no per-kind comparator is supplied. This is the only
 * comparator shipped with Ticket 4. Ticket 2 supplies a kit-position
 * comparator for `Items en esta publicación` via the pluggable predicate.
 */
export const defaultLabelComparator: VariableComparator = ( a, b ) =>
  a.label.localeCompare( b.label );

/**
 * Build a kind-aware comparator from a per-kind override map.
 *
 * Returns a single comparator that defers to the per-kind override when
 * both inputs share a `kind` matched in `overrides`, falling back to
 * `defaultLabelComparator` otherwise. This is the integration seam Ticket
 * 2 will use:
 *
 * ```ts
 * const cmp = kindAwareComparator({
 *   'Items en esta publicación': kitPositionComparator( kit )
 * });
 * groupVariablesByKind( vars, cmp );
 * ```
 *
 * No allocation if `overrides` is empty — falls through to the default.
 */
export function kindAwareComparator(
  overrides: Record<string, VariableComparator>
): VariableComparator {
  return ( a, b ) => {
    const kind = a.kind ?? GENERAL_KIND;
    if ( kind === ( b.kind ?? GENERAL_KIND ) && overrides[ kind ]) {
      return overrides[ kind ]( a, b );
    }
    return defaultLabelComparator( a, b );
  };
}

/**
 * Group materialized variables by `expose.kind`, preserving first-seen
 * insertion order at the group level and sorting chips within each
 * group via `comparator` (defaults to alphabetical by label).
 *
 * Acceptance criteria covered:
 *   1. One section per unique `expose.kind` value.
 *   2. Chips without `kind` collapse into a single "General" section.
 *   3. Within-section sort uses the supplied comparator (alphabetical by
 *      default; Ticket 2 will pass a kit-position comparator for
 *      `Items en esta publicación`).
 */
export function groupVariablesByKind(
  variables: readonly MaterializedVariable[],
  comparator: VariableComparator = defaultLabelComparator
): VariableGroup[] {
  const groupMap = new Map<string, MaterializedVariable[]>();

  for ( const variable of variables ) {
    const kind = variable.kind && variable.kind.trim().length > 0
      ? variable.kind
      : GENERAL_KIND;
    const bucket = groupMap.get( kind );
    if ( bucket ) {
      bucket.push( variable );
    } else {
      groupMap.set( kind, [variable]);
    }
  }

  const groups: VariableGroup[] = [];
  for ( const [ kind, bucket ] of groupMap ) {
    const sorted = [...bucket].sort( comparator );
    groups.push({ kind, variables: sorted });
  }
  return groups;
}

/**
 * Flatten `Record<string, MaterializedVariable[]>` (the legacy
 * `variables` shape passed to FormulaInput / TemplateInput) into the
 * single array `groupVariablesByKind` consumes.
 *
 * Behaviour:
 *   - Falls back to the outer record key when `expose.kind` is absent —
 *     this preserves today's grouping for any legacy declaration that
 *     relied on the outer key as the section header.
 *   - Filters out malformed entries (missing label/value) defensively.
 */
export function flattenVariablesRecord(
  variables: Record<string, readonly MaterializedVariable[]>
): MaterializedVariable[] {
  const out: MaterializedVariable[] = [];
  for ( const [ groupName, options ] of Object.entries( variables )) {
    if ( !Array.isArray( options )) continue;
    for ( const option of options ) {
      if ( !option || typeof option !== 'object' ) continue;
      if ( typeof option.label !== 'string' || typeof option.value !== 'string' ) continue;
      out.push({
        kind: option.kind && option.kind.trim().length > 0 ? option.kind : groupName,
        label: option.label,
        value: option.value
      });
    }
  }
  return out;
}
